import React, { useMemo } from "react";
import axios from "axios";
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { getSessionUserId, getSessionEmpresaId } from "../../utils/SessionUser";
import { iconToFA } from "../../utils/iconHelpers";

export const SigmaSidebar = ({
  menu,
  activeLink, // Clave del menú
  activeId,   // ID del menú (más fiable para resaltar)
  expandedState,
  activeTop,
  onToggleExpand,
  onClickLink,
  loading,
  sidebarOpen,
}) => {

  // 🔹 Encontrar el camino de padres para auto-expandir
  const autoExpandedPaths = useMemo(() => {
    const paths = new Set();
    if (!menu || (!activeLink && !activeId)) return paths;

    const findActiveAndTrackParents = (nodes, currentPath = "") => {
      for (const node of nodes) {
        // Usamos el ID como clave de ruta para evitar problemas con títulos duplicados o caracteres especiales
        const pathIdentifier = String(node.id);
        const path = currentPath ? `${currentPath}/${pathIdentifier}` : pathIdentifier;

        const nodeClave = String(node.clave || "").trim();
        const activeLinkTrim = String(activeLink || "").trim();

        // Prioridad absoluta al ID, fallback a la clave
        const isThisActive = (activeId && String(node.id) === String(activeId)) ||
          (activeLinkTrim && nodeClave === activeLinkTrim);

        if (isThisActive) return true;

        if (node.children && node.children.length > 0) {
          if (findActiveAndTrackParents(node.children, path)) {
            paths.add(path);
            return true;
          }
        }
      }
      return false;
    };

    findActiveAndTrackParents(menu);
    return paths;
  }, [menu, activeLink, activeId]);

  const isExpanded = (path) => {
    // 1. Prioridad: Si el usuario ha interactuado explícitamente con este nodo
    const userDefined = expandedState?.[activeTop]?.[path];
    if (typeof userDefined === "boolean") return userDefined;

    // 2. Fallback: Auto-expandir si es parte del camino al elemento activo
    return autoExpandedPaths.has(path);
  };

  const renderNode = (node, level = 0, parentPath = "") => {
    // Usamos ID en el path interno para mayor estabilidad
    const pathIdentifier = String(node.id);
    const path = parentPath ? `${parentPath}/${pathIdentifier}` : pathIdentifier;

    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const expanded = isExpanded(path);

    const nodeClave = String(node.clave || "").trim();
    const activeLinkTrim = String(activeLink || "").trim();

    const isActive = (activeId && String(node.id) === String(activeId)) ||
      (activeLinkTrim && nodeClave === activeLinkTrim);

    const faClass = iconToFA(node.icono) || "fa-regular fa-circle";

    return (
      <div key={path} className={`sk-node sk-level-${level} ${isActive ? "active is-active" : ""}`}>
        <div
          className={`sk-link sk-node-header ${isActive ? "sk-link--active active is-active" : ""}`}
          onClick={() => {
            if (hasChildren) onToggleExpand(path, expanded);
            onClickLink({ ...node, path, hasChildren });
          }}
          data-testid={`menu-node-${node.id}`}
        >
          <span className="sk-node-icon" aria-hidden="true">
            <i className={faClass} />
          </span>

          <span className="sk-node-label" title={node.title}>{node.title}</span>

          {hasChildren ? (
            <span className={`sk-caret ${expanded ? "open" : ""}`} />
          ) : (
            <span />
          )}
        </div>

        {hasChildren && expanded && (
          <div className="sk-children">
            {node.children.map((child) => renderNode(child, level + 1, path))}
          </div>
        )}
      </div>
    );
  };

  // Estado para el usuario que se muestra en el pie del sidebar
  const [userInfo, setUserInfo] = React.useState({
    nombre: "Usuario",
    empresa: "Sin Empresa Asignada",
    avatar: null
  });

  React.useEffect(() => {
    // Fallback rápido desde sessionStorage
    const sessionRaw = sessionStorage.getItem('sigmaUser');
    if (sessionRaw) {
      try {
        const session = JSON.parse(sessionRaw);
        setUserInfo(prev => ({
          ...prev,
          nombre: session.nombreUsuario || session.NombreUsuario || prev.nombre
        }));
      } catch (e) { }
    }

    const fetchUserProfile = async () => {
      try {
        const uid = getSessionUserId();
        if (!uid) return;

        // 1. Obtener datos básicos del usuario (detectar casing)
        const resUser = await axios.get(ENDPOINTS.usuarioById(uid));
        const u = resUser.data;

        const idPersona = u.idPersona ?? u.IdPersona;
        const nombreUsr = u.nombreUsuario ?? u.NombreUsuario;
        const rolUsr = u.rol ?? u.Rol ?? "Administrador";

        if (idPersona) {
          // 2. Obtener datos de la persona (Nombre Completo y Foto)
          const resPerson = await axios.get(ENDPOINTS.personasById(idPersona));
          const p = resPerson.data;

          const nom = p.nombre ?? p.Nombre ?? "";
          const apP = p.apellidoPaterno ?? p.ApellidoPaterno ?? "";
          const apM = p.apellidoMaterno ?? p.ApellidoMaterno ?? "";
          const foto = p.fotoPerfil ?? p.FotoPerfil;

          const fullName = `${nom} ${apP} ${apM}`.trim();

          // Helper para URL de imagen (debe apuntar a la raíz del servidor)
          const getImageUrl = (path) => {
            if (!path) return null;
            if (path.startsWith('http')) return path;
            // Quitamos /api del final del URL base si existe
            const server = ENDPOINTS.usuarios.split('/api')[0];
            return `${server}${path}`;
          };

          setUserInfo({
            nombre: fullName || nombreUsr || "Usuario",
            empresa: userInfo.empresa,
            avatar: getImageUrl(foto)
          });
        } else if (nombreUsr) {
          setUserInfo(prev => ({ ...prev, nombre: nombreUsr }));
        }

        // Obtener nombre de empresa desde la sesión
        const idEmpresa = getSessionEmpresaId();
        if (idEmpresa) {
          try {
            const apiBase = ENDPOINTS.usuarios.split('/api')[0];
            const resEmp = await axios.get(`${apiBase}/api/Empresas/${idEmpresa}`);
            const emp = resEmp.data;
            const nombreEmp = emp.nombre ?? emp.Nombre ?? null;
            if (nombreEmp) setUserInfo(prev => ({ ...prev, empresa: nombreEmp }));
          } catch { /* sin empresa asignada, se muestra Global */ }
        }
      } catch (err) {
        console.error("[SigmaSidebar] Error fetching user profile:", err);
      }
    };
    fetchUserProfile();
  }, []);

  // Ref para el contenedor del scroll
  const scrollRef = React.useRef(null);

  // Auto-scroll al elemento activo
  React.useEffect(() => {
    if (!loading && (activeId || activeLink)) {
      // Un pequeño delay para asegurar que el DOM esté listo y las carpetas expandidas
      const timer = setTimeout(() => {
        if (scrollRef.current) {
          const activeEl = scrollRef.current.querySelector('.sk-link--active');
          if (activeEl) {
            activeEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start'
            });
          }
        }
      }, 400); // 400ms para dar tiempo a las animaciones de expansión
      return () => clearTimeout(timer);
    }
  }, [loading, activeId, activeLink, activeTop]);

  return (
    <aside className={`sk-sidebar ${!sidebarOpen ? 'sk-sidebar--closed' : ''}`} aria-label="Menú principal">
      <div className="sk-sidebar__scroll" ref={scrollRef}>
        {loading ? (
          <div className="sk-sidebar-loading">Cargando menús...</div>
        ) : menu && menu.length > 0 ? (
          menu.map((node) => renderNode(node))
        ) : (
          <div className="sk-sidebar-empty">No hay opciones disponibles</div>
        )}
      </div>

      {/* Pie del Sidebar con el usuario REAL (Nombre completo y Foto) */}
      <div className="sk-sidebar__footer">
        <div className="sk-sidebar-user">
          <div className="sk-sidebar-user__avatar">
            {userInfo.avatar ? (
              <img
                src={userInfo.avatar}
                alt="Profile"
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              userInfo.nombre.charAt(0).toUpperCase()
            )}
          </div>
          <div className="sk-sidebar-user__info">
            <div className="sk-sidebar-user__name" title={userInfo.nombre}>{userInfo.nombre}</div>
            <div className="sk-sidebar-user__role">{userInfo.empresa || 'Sin Empresa Asignada'}</div>
          </div>
        </div>
      </div>
    </aside>
  );
};
